import React, { useState, useEffect, useContext } from 'react';
import styles from './styles.module.scss';
import SideModal from 'src/components/SideModal';
import Input, { Label } from 'src/components/Input';
import ModalFooter from 'src/components/ModalFooter';
import ModalHeader from 'src/components/ModalHeader';
import FacilityStore, { Facility } from 'src/stores/FacilityStore';
import { ToastStoreObject, ToastType } from 'src/stores/ToastStore';
import * as utils from 'src/utils';
import Flex from '../Flex';
import UserStore from 'src/stores/UserStore';
import OrgStore from 'src/stores/OrgStore';
import Checkbox from 'src/components/Checkbox';
import { observer } from 'mobx-react';
import Select from 'react-select';
import { DEFAULT_FACILITY_TIME_ZONE, FACILITY_TIMEZONES } from 'src/utils/facilityTimeZones';
import {
    CAPTURE_AI_SCAN_TYPES_FACILITY_OPTION_NAME,
    CaptureAiMode,
    FACILITY_OPTION_CONFIG,
    FacilityOptionConfig,
    LEGACY_CAPTURE_AI_FACILITY_OPTION_NAME,
    resolveCaptureAiMode,
} from 'common/lib/model/facility/FacilityOptions';
import { formatFacilityOptionValueForEditing, formatFacilityOptionValueForSubmit } from 'src/utils/facilityOptions';
import * as facilitiesApi from 'src/api/facilities';
import ConfirmationModal from 'src/components/ConfirmationModal';

export interface FacilityModalProps {
    facility: Facility;
    onClose: Function;
    onSave: Function;
}

const defaultFacility: Facility = {
    facNm: '',
    facNmIntrnl: '',
    facDesc: '',
    addrLn1: '',
    addrLn2: '',
    addrZipCd: '',
    addrStCd: '',
    phNoMain: '',
    addrCityNm: '',
    facilityTypeCode: '',
    facilityNumber: '',
    gpsLocation: '',
    nationalGridCoordinates: '',
    cityCode: '',
    stateCode: '',
    countyCode: '',
    countryCode: '',
    facilityPhones: [],
};

function FacilityModal(props: FacilityModalProps) {
    const [data, setData] = useState<Facility>(props.facility || { ...defaultFacility });
    const isEdit = !!props.facility;
    const [facilityTimeZone, setFacilityTimeZone] = useState(DEFAULT_FACILITY_TIME_ZONE);
    const [facilityOptionValues, setFacilityOptionValues] = useState<Record<string, string>>(
        buildDefaultFacilityOptionValues(),
    );
    const [initialFacilityOptionValues, setInitialFacilityOptionValues] = useState<Record<string, string>>(
        buildDefaultFacilityOptionValues(),
    );
    const [isLoadingFacilityOptions, setIsLoadingFacilityOptions] = useState(isEdit);
    const [captureAiV2Readiness, setCaptureAiV2Readiness] = useState<facilitiesApi.CaptureAiV2FacilityReadiness | null>(
        null,
    );
    const [isLoadingCaptureAiV2Readiness, setIsLoadingCaptureAiV2Readiness] = useState(false);
    const [confirmV2Activation, setConfirmV2Activation] = useState(false);
    const userStore = useContext(UserStore);
    const orgStore = useContext(OrgStore);
    const facilityStore = useContext(FacilityStore);
    const captureAiV2Entitled = orgStore.orgCaptureAiScanTypesOptions.some(
        (option) => `${option.orgId}` === `${userStore.selectedOrg.orgId}` && option.optVal?.toLowerCase() === 'true',
    );

    const captureAiMode = resolveCaptureAiMode(
        facilityOptionValues[LEGACY_CAPTURE_AI_FACILITY_OPTION_NAME]?.toLowerCase() === 'true',
        facilityOptionValues[CAPTURE_AI_SCAN_TYPES_FACILITY_OPTION_NAME]?.toLowerCase() === 'true',
    );
    const initialCaptureAiMode = resolveCaptureAiMode(
        initialFacilityOptionValues[LEGACY_CAPTURE_AI_FACILITY_OPTION_NAME]?.toLowerCase() === 'true',
        initialFacilityOptionValues[CAPTURE_AI_SCAN_TYPES_FACILITY_OPTION_NAME]?.toLowerCase() === 'true',
    );

    useEffect(() => {
        let isMounted = true;
        async function loadFacilityOptions() {
            const defaultValues = buildDefaultFacilityOptionValues();
            if (!isEdit || !data.facId) {
                setFacilityOptionValues(defaultValues);
                setInitialFacilityOptionValues(defaultValues);
                setIsLoadingFacilityOptions(false);
                return;
            }

            setIsLoadingFacilityOptions(true);
            try {
                const optionEntries = await Promise.all(
                    FACILITY_OPTION_CONFIG.map(async (optionConfig) => {
                        const option = await facilityStore.getFacilityOption(data.facId, optionConfig.optionName);
                        return [
                            optionConfig.optionName,
                            formatFacilityOptionValueForEditing({
                                inputType: optionConfig.inputType,
                                optionValue: option?.optVal ?? optionConfig.defaultValue,
                            }),
                        ] as const;
                    }),
                );
                const loadedValues = optionEntries.reduce<Record<string, string>>((acc, [optionName, optionValue]) => {
                    acc[optionName] = optionValue;
                    return acc;
                }, {});
                if (isMounted) {
                    setFacilityOptionValues(loadedValues);
                    setInitialFacilityOptionValues(loadedValues);
                }
            } catch (error) {
                if (isMounted) {
                    setFacilityOptionValues(defaultValues);
                    setInitialFacilityOptionValues(defaultValues);
                    ToastStoreObject.show(utils.parseError(error), ToastType.Error);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingFacilityOptions(false);
                }
            }
        }

        loadFacilityOptions();

        return () => {
            isMounted = false;
        };
    }, [data.facId, isEdit]);

    async function loadCaptureAiV2Readiness() {
        if (!isEdit || !data.facId || !captureAiV2Entitled) {
            setCaptureAiV2Readiness(null);
            return null;
        }
        setIsLoadingCaptureAiV2Readiness(true);
        try {
            const readiness = await facilitiesApi.fetchCaptureAiV2Readiness(data.facId);
            setCaptureAiV2Readiness(readiness);
            return readiness;
        } catch (error) {
            setCaptureAiV2Readiness(null);
            ToastStoreObject.show(utils.parseError(error), ToastType.Error);
            return null;
        } finally {
            setIsLoadingCaptureAiV2Readiness(false);
        }
    }

    async function updateCaptureAiMode(mode: CaptureAiMode) {
        setFacilityOptionValues((current) => ({
            ...current,
            [LEGACY_CAPTURE_AI_FACILITY_OPTION_NAME]: mode === 'v1' ? 'true' : 'false',
            [CAPTURE_AI_SCAN_TYPES_FACILITY_OPTION_NAME]: mode === 'v2' ? 'true' : 'false',
        }));
        if (mode === 'v2') {
            await loadCaptureAiV2Readiness();
        } else {
            setCaptureAiV2Readiness(null);
        }
    }

    async function persistFacility() {
        try {
            const facilityOptions = getFacilityOptionPayload(!isEdit);
            const facilityPayload = {
                ...data,
                captureAiMode,
                ...(Object.keys(facilityOptions).length ? { facilityOptions } : {}),
            };
            if (isEdit) {
                await facilityStore.updateFacility(data.facId, facilityPayload);
            } else {
                await facilityStore.createFacility(Object.assign(facilityPayload, { facilityTimeZone }));
            }

            props.onSave();
            props.onClose();
        } catch (e) {
            ToastStoreObject.show(utils.parseError(e), ToastType.Error);
        }
    }

    async function handleSave() {
        if (captureAiMode === 'v2' && initialCaptureAiMode !== 'v2') {
            const readiness = captureAiV2Readiness || (await loadCaptureAiV2Readiness());
            if (!readiness?.canActivateV2) {
                ToastStoreObject.show('Resolve the Capture AI v2 readiness blockers before saving.', ToastType.Error);
                return;
            }
            setConfirmV2Activation(true);
            return;
        }
        await persistFacility();
    }

    const updateInput = (param: string) => (t: string) => setData({ ...data, [param]: t });

    function updateFacilityOption(optionName: string, optionValue: string) {
        setFacilityOptionValues({ ...facilityOptionValues, [optionName]: optionValue });
    }

    function getFacilityOptionPayload(includeUnchanged: boolean = false) {
        return FACILITY_OPTION_CONFIG.reduce<Record<string, boolean | string>>((payload, optionConfig) => {
            if (
                optionConfig.optionName === LEGACY_CAPTURE_AI_FACILITY_OPTION_NAME ||
                optionConfig.optionName === CAPTURE_AI_SCAN_TYPES_FACILITY_OPTION_NAME
            ) {
                return payload;
            }
            const optionValue = formatFacilityOptionValueForSubmit(
                optionConfig,
                facilityOptionValues[optionConfig.optionName] ?? optionConfig.defaultValue,
            );
            const initialOptionValue = formatFacilityOptionValueForSubmit(
                optionConfig,
                initialFacilityOptionValues[optionConfig.optionName] ?? optionConfig.defaultValue,
            );
            if (!includeUnchanged && optionValue === initialOptionValue) {
                return payload;
            }
            payload[optionConfig.optionName] =
                optionConfig.inputType === 'boolean' ? optionValue === 'true' : optionValue;
            return payload;
        }, {});
    }

    function renderFacilitySettings() {
        return (
            <div className={styles.facilitySettings}>
                <div className={styles.facilitySettingsTitle}>
                    <h3>Facility Settings</h3>
                </div>
                <div className={styles.facilitySettingsTable}>
                    <div className={styles.facilitySettingsHeader}>
                        <span>Setting</span>
                        <span>Value</span>
                    </div>
                    <div className={styles.facilitySettingsRow} data-test-id="facility-capture-ai-mode-row">
                        <span>
                            <strong>Capture AI Mode</strong>
                            <small>
                                Capture AI v2 replaces v1 for this facility after its document types are ready.
                            </small>
                        </span>
                        <div>
                            <select
                                value={captureAiMode}
                                disabled={isLoadingFacilityOptions || isLoadingCaptureAiV2Readiness}
                                onChange={(event) => updateCaptureAiMode(event.target.value as CaptureAiMode)}
                                data-test-id="facility-capture-ai-mode"
                                aria-label="Capture AI Mode"
                            >
                                <option value="off">Off</option>
                                <option value="v1">Capture AI v1</option>
                                {captureAiV2Entitled && isEdit ? (
                                    <option value="v2">Capture AI v2 (Document Types)</option>
                                ) : null}
                            </select>
                            {!captureAiV2Entitled ? (
                                <small>Capture AI v2 is not enabled for this organization.</small>
                            ) : null}
                            {captureAiMode === 'v2' && isLoadingCaptureAiV2Readiness ? (
                                <small>Checking Capture AI v2 readiness...</small>
                            ) : null}
                            {captureAiMode === 'v2'
                                ? captureAiV2Readiness?.blockers.map((blocker) => (
                                      <small className={styles.readinessBlocker} key={blocker.code}>
                                          {blocker.message}
                                      </small>
                                  ))
                                : null}
                            {captureAiMode === 'v2'
                                ? captureAiV2Readiness?.warnings.map((warning) => (
                                      <small className={styles.readinessWarning} key={warning.code}>
                                          {warning.message}
                                      </small>
                                  ))
                                : null}
                            {captureAiMode === 'v2' && captureAiV2Readiness?.canActivateV2 ? (
                                <small className={styles.readinessReady}>
                                    Ready: {captureAiV2Readiness.publishedUsableDocumentTypeCount} published document
                                    type
                                    {captureAiV2Readiness.publishedUsableDocumentTypeCount === 1 ? '' : 's'}.
                                </small>
                            ) : null}
                        </div>
                    </div>
                    {FACILITY_OPTION_CONFIG.filter(
                        (optionConfig) =>
                            optionConfig.optionName !== LEGACY_CAPTURE_AI_FACILITY_OPTION_NAME &&
                            optionConfig.optionName !== CAPTURE_AI_SCAN_TYPES_FACILITY_OPTION_NAME,
                    ).map((optionConfig) => (
                        <div key={optionConfig.optionName} className={styles.facilitySettingsRow}>
                            <span>
                                <strong>{optionConfig.displayName}</strong>
                                <small>{optionConfig.description}</small>
                            </span>
                            {renderFacilityOptionInput(optionConfig)}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    function renderFacilityOptionInput(optionConfig: FacilityOptionConfig) {
        const optionValue = facilityOptionValues[optionConfig.optionName] ?? optionConfig.defaultValue;
        const accessibilityProps = { 'aria-label': optionConfig.displayName };
        if (optionConfig.inputType === 'boolean') {
            return (
                <Checkbox
                    {...accessibilityProps}
                    checked={optionValue.toLowerCase() === 'true'}
                    disabled={isLoadingFacilityOptions}
                    data-test-id={getFacilityOptionTestId(optionConfig.optionName)}
                    onChange={(event) =>
                        updateFacilityOption(optionConfig.optionName, event.target.checked ? 'true' : 'false')
                    }
                />
            );
        }

        if (optionConfig.inputType === 'email-list') {
            return (
                <Input
                    {...accessibilityProps}
                    multiLine
                    rows={4}
                    value={optionValue}
                    disabled={isLoadingFacilityOptions}
                    data-test-id={getFacilityOptionTestId(optionConfig.optionName)}
                    onChangeText={(value: string) => updateFacilityOption(optionConfig.optionName, value)}
                />
            );
        }

        return (
            <Input
                {...accessibilityProps}
                type={optionConfig.inputType === 'date' ? 'date' : 'text'}
                value={optionValue}
                placeholder={optionConfig.inputType === 'datetime' ? 'YYYY-MM-DD HH:mm:ss' : ''}
                disabled={isLoadingFacilityOptions}
                data-test-id={getFacilityOptionTestId(optionConfig.optionName)}
                onChangeText={(value: string) => updateFacilityOption(optionConfig.optionName, value)}
            />
        );
    }

    function getFacilityOptionTestId(optionName: string) {
        if (optionName === CAPTURE_AI_SCAN_TYPES_FACILITY_OPTION_NAME) {
            return 'facility-capture-ai-scan-types-enabled';
        }
        return `facility-option-${optionName}`;
    }

    return (
        <SideModal isOpen={true} onClose={() => props.onClose()}>
            <ModalHeader
                title={props.facility ? `Edit ${data.facNm || 'Facility'}` : 'New Facility'}
                onClose={() => props.onClose()}
            />
            <div className={styles.contentWrap}>
                <Flex direction="column" className={styles.form}>
                    <Flex direction="row" justify="between" className={styles.formRow}>
                        <Flex value={1}>
                            <Input
                                className={styles.input}
                                label="Name"
                                value={data.facNm}
                                onChangeText={updateInput('facNm')}
                            />
                        </Flex>
                    </Flex>
                    <Flex direction="row" justify="between" className={styles.formRow}>
                        <Flex value={1}>
                            <Input
                                className={styles.input}
                                label="Internal Name"
                                value={data.facNmIntrnl}
                                disabled={isEdit}
                                onChangeText={(v: string) => {
                                    const filteredValue = v.replace(/[^a-zA-Z]/g, '').toUpperCase();
                                    setData({ ...data, facNmIntrnl: filteredValue });
                                }}
                            />
                        </Flex>
                    </Flex>
                    {!isEdit && (
                        <Flex direction="row" justify="between" className={styles.formRow}>
                            <Flex value={1}>
                                <Label text="Time Zone" className={styles.labelWrap} />
                                <Select
                                    value={FACILITY_TIMEZONES.find((timezone) => timezone.value === facilityTimeZone)}
                                    placeholder="Select Time Zone"
                                    onChange={(s: any) => setFacilityTimeZone(s.value)}
                                    options={FACILITY_TIMEZONES}
                                    styles={utils.styleSelectComponent(true)}
                                    menuPortalTarget={document.body}
                                />
                            </Flex>
                        </Flex>
                    )}
                    <Flex direction="row" justify="between" className={styles.formRow}>
                        <Flex value={1}>
                            <Input
                                className={styles.input}
                                label="Description"
                                value={data.facDesc}
                                onChangeText={updateInput('facDesc')}
                            />
                        </Flex>
                    </Flex>
                    <Flex direction="row" justify="between" className={styles.formRow}>
                        <Flex value={1}>
                            <Input
                                className={styles.input}
                                label="Address 1"
                                value={data.addrLn1}
                                onChangeText={updateInput('addrLn1')}
                            />
                        </Flex>
                    </Flex>
                    <Flex direction="row" justify="between" className={styles.formRow}>
                        <Flex value={1}>
                            <Input
                                className={styles.input}
                                label="Address 2"
                                value={data.addrLn2}
                                onChangeText={updateInput('addrLn2')}
                            />
                        </Flex>
                    </Flex>
                    <Flex direction="row" justify="between" className={styles.formRow}>
                        <Flex value={1}>
                            <Input
                                className={styles.input}
                                label="City"
                                value={data.addrCityNm}
                                onChangeText={updateInput('addrCityNm')}
                            />
                        </Flex>
                        <Flex value={0.1} />
                        <Flex value={1}>
                            <Input
                                className={styles.input}
                                label="State"
                                value={data.addrStCd}
                                onChangeText={updateInput('addrStCd')}
                            />
                        </Flex>
                        <Flex value={0.1} />
                        <Flex value={1}>
                            <Input
                                className={styles.input}
                                label="Zip Code"
                                value={data.addrZipCd}
                                onChangeText={updateInput('addrZipCd')}
                            />
                        </Flex>
                    </Flex>
                    <Flex direction="row" justify="between" className={styles.formRow}>
                        <Flex value={1}>
                            <Input
                                className={styles.input}
                                label="Main Phone Number"
                                value={data.phNoMain}
                                onChangeText={updateInput('phNoMain')}
                            />
                        </Flex>
                    </Flex>
                    {renderFacilitySettings()}
                </Flex>
            </div>
            <ModalFooter
                primaryText={isEdit ? 'Save' : 'Create'}
                primaryClick={handleSave}
                primaryProps={{
                    disabled:
                        isLoadingFacilityOptions ||
                        isLoadingCaptureAiV2Readiness ||
                        (captureAiMode === 'v2' && captureAiV2Readiness?.canActivateV2 === false),
                }}
                secondaryClick={() => {
                    setData({ ...defaultFacility });
                    props.onClose();
                }}
            />
            {confirmV2Activation ? (
                <ConfirmationModal
                    headerTitle="Activate Capture AI v2?"
                    title="Capture AI v2 will replace Capture AI v1 for this facility. Published document types and overrides will become available to the mobile app."
                    confirmButtonText="Activate v2"
                    cancelButtonText="Cancel"
                    onCancel={() => setConfirmV2Activation(false)}
                    onConfirm={async () => {
                        setConfirmV2Activation(false);
                        await persistFacility();
                    }}
                />
            ) : null}
        </SideModal>
    );
}

function buildDefaultFacilityOptionValues() {
    return FACILITY_OPTION_CONFIG.reduce<Record<string, string>>((values, optionConfig) => {
        values[optionConfig.optionName] = formatFacilityOptionValueForEditing({
            inputType: optionConfig.inputType,
            optionValue: optionConfig.defaultValue,
        });
        return values;
    }, {});
}

export default observer(FacilityModal);
